import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import {
  getProduct,
  getStorefrontTextileProduct,
  getTextileProducts
} from "../services/productService";
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { useWishlist } from '../contexts/WishlistContext';
import { useToast } from '../contexts/ToastContext';
import formatPrice from '../utils/formatPrice';
import "../css/productdetails.css";
import "../css/Sizepopup.css";
import { getProductReviews } from "../services/reviewService";
import { getAlsoBought, getSizeChart } from "../services/discoveryService";
import { addRecentlyViewed, getRecentlyViewed } from "../utils/recentlyViewed";
import ProductRail from "../components/product/ProductRail";
import ReviewsSection from "../components/review/ReviewsSection";
import { applySeo, absUrl, clip } from "../utils/seo";
import { mediaUrl } from "../utils/mediaUrl";
import { swatchBackground } from "../utils/colors";
import { getCheckoutConfig } from "../services/orderService";
import { trackViewItem } from "../utils/analytics";


import ProductCard from "../components/product/ProductCard.jsx";
import DeliveryCheck from "../components/product/DeliveryCheck.jsx";

import {
  TbShirt,
  TbAlertTriangle,
  TbWash,
  TbRefresh,
  TbBuildingFactory2,
} from "react-icons/tb";


// ⚠️ Standard women's measurements (inches). Replace with your own
// brand's chart if it differs — it drives the size guide and size labels.
const SIZE_CHART = [
  { size: 'XS', bust: 32, waist: 26, hip: 35 },
  { size: 'S', bust: 34, waist: 28, hip: 37 },
  { size: 'M', bust: 36, waist: 30, hip: 39 },
  { size: 'L', bust: 38, waist: 32, hip: 41 },
  { size: 'XL', bust: 40, waist: 34, hip: 43 },
  { size: 'XXL', bust: 42, waist: 36, hip: 45 },
  { size: '3XL', bust: 44, waist: 38, hip: 47 },
  { size: '4XL', bust: 46, waist: 40, hip: 49 }
];
const SIZE_CHEST_MAP = Object.fromEntries(
  SIZE_CHART.flatMap((r) => {
    const label = `Bust: ${r.bust} in`;
    return r.size === 'XXL' ? [['XXL', label], ['2XL', label]] : [[r.size, label]];
  })
);

// Standard size display order: S M L XL XXL ...
const SIZE_ORDER = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '2XL', '3XL', '4XL', '5XL'];

const sortSizes = (sizes) =>
  [...sizes].sort((a, b) => {
    const ai = SIZE_ORDER.indexOf(String(a).toUpperCase());
    const bi = SIZE_ORDER.indexOf(String(b).toUpperCase());
    // unknown sizes (numeric like 30/32/34 or "Free Size") go after the
    // lettered sizes, sorted naturally among themselves
    if (ai === -1 && bi === -1) {
      return String(a).localeCompare(String(b), undefined, { numeric: true });
    }
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });

// Care instructions that suit the fabric (silk must never be machine washed)
const washCareFor = (p) => {
  if (Array.isArray(p.washCare) && p.washCare.length) return p.washCare;
  const m = String(p.material || '').toLowerCase();
  if (/silk|zari|tissue|banarasi|kanjiv|kanchi/.test(m + ' ' + String(p.productType || '').toLowerCase())) {
    return ['Dry clean only', 'Store folded in a muslin or cotton cloth', 'Refold every few months to protect the zari', 'Keep away from perfume and direct sunlight'];
  }
  if (/cotton|linen|khadi/.test(m)) {
    return ['Hand wash or gentle machine wash in cold water', 'Wash dark colours separately; the first wash may bleed', 'Dry in shade', 'Warm iron'];
  }
  if (/georgette|chiffon|crepe|net|organza/.test(m)) {
    return ['Hand wash gently in cold water or dry clean', 'Do not wring', 'Dry flat in shade', 'Cool iron on the reverse'];
  }
  return ['Gentle wash in cold water', 'Wash dark colours separately', 'Do not bleach', 'Dry in shade; warm iron if needed'];
};

const ACCORDION_SECTIONS = [
  {
    key: "details",
    icon: <TbShirt />,
    title: "Product Details",
    subtitle: "",
  },
  {
    key: "disclaimer",
    icon: <TbAlertTriangle />,
    title: "Product Disclaimer",
    subtitle: "Minor variations in size (±5%) and color may occur.",
  },
  {
    key: "washcare",
    icon: <TbWash />,
    title: "Wash Care",
    subtitle: "",
  },
  {
    key: "returns",
    icon: <TbRefresh />,
    title: "Return & Refund Policy",
    subtitle: "",
  },
  {
    key: "manufacturing",
    icon: <TbBuildingFactory2 />,
    title: "Manufacturing Details",
    subtitle: "",
  },
];

const ProductDetailsPage = () => {
  const { id } = useParams();
  const { search } = useLocation();
  // Readable URLs (/product/kanchipuram-silk-saree) are always textile products
  const isTextileProduct =
    new URLSearchParams(search).get('textile') === 'true' || !/^[a-f0-9]{24}$/i.test(String(id || ''));
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { user } = useAuth();
  const { isInWishlist, toggleWishlist, loadingIds } = useWishlist();
  const { addToast } = useToast();

  const [product, setProduct] = useState(null);
  const [size, setSize] = useState('');
  const [color, setColor] = useState('');
  const [qty, setQty] = useState(1);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [activeAccordion, setActiveAccordion] = useState('details');
  const [wishlisted, setWishlisted] = useState(false);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [showSizePopup, setShowSizePopup] = useState(false);
  const [showSizeGuide, setShowSizeGuide] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [alsoBought, setAlsoBought] = useState([]);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [sizeChart, setSizeChart] = useState(null);
  const [returnDays, setReturnDays] = useState(7);

  useEffect(() => {
    getCheckoutConfig()
      .then((c) => c?.returnWindowDays && setReturnDays(c.returnWindowDays))
      .catch(() => {});
  }, []);

  const toggleAccordion = (section) => {
    setActiveAccordion(activeAccordion === section ? null : section);
  };

  useEffect(() => {
    // Already showing this product (we just swapped the id URL for its slug)
    if (product && (product.slug === id || String(product._id) === String(id))) return;

    window.scrollTo(0, 0);

    setSelectedImageIndex(0);
    setActiveAccordion('details');

    const load = async () => {
      try {
        const rawProduct = isTextileProduct
          ? await getStorefrontTextileProduct(id)
          : await getProduct(id);

        const variants = rawProduct.variants || [];
        const firstVariant = variants[0] || null;

        const p = isTextileProduct
          ? {
            ...rawProduct,
            price:
              variants.length > 0
                ? Math.min(...variants.map(v => Number(v.retailPrice)))
                : Number(rawProduct.retailPrice || 0),
            mrp:
              variants.length > 0
                ? Math.max(...variants.map(v => Number(v.mrp)))
                : Number(rawProduct.mrp || 0),
            // sizes always shown in S M L XL XXL ... order
            sizes:
              variants.length > 0
                ? sortSizes(variants.map(v => v.size))
                : sortSizes(rawProduct.sizes || []),
            // size -> stockAvailable map, so out-of-stock sizes can be
            // shown disabled instead of silently orderable
            sizeStock:
              variants.length > 0
                ? Object.fromEntries(
                  variants.map(v => [v.size, Number(v.stockAvailable) || 0])
                )
                : {},
            colors:
              rawProduct.colors?.length
                ? rawProduct.colors
                : rawProduct.color
                  ? [rawProduct.color]
                  : [],
            style: rawProduct.sleeveOrStyle,
            // NOTE: gst, sku and warehouseName live on each variant, not on
            // the product itself. Fall back to the first variant here;
            // once a size is picked, the JSX below re-resolves these from
            // the variant that actually matches the selected size.
            gst: firstVariant?.gst,
            sku: firstVariant?.sku,
            warehouseName: rawProduct.warehouseName || firstVariant?.warehouseName,
            supplierName: rawProduct.supplierName || firstVariant?.supplierName,
          }
          : {
            ...rawProduct,
            sizes: sortSizes(rawProduct.sizes || []),
          };

        setProduct(p);

        // Old /product/<id>?textile=true links -> readable /product/<slug>
        if (p.slug && id !== p.slug) {
          navigate(`/product/${p.slug}`, { replace: true });
        }

        // SEO: title, description, share image and Product structured data
        const prices = (p.variants || []).map((v) => Number(v.retailPrice)).filter(Boolean);
        const low = prices.length ? Math.min(...prices) : Number(p.price) || null;
        const inStock = (p.variants || []).some((v) => (v.stockAvailable || 0) > 0);
        const colour = (p.colors || [])[0] || '';
        const path = p.slug ? `/product/${p.slug}` : `/product/${p._id}`;
        applySeo({
          title: `${clip(`${p.name}${colour && !p.name.toLowerCase().includes(colour.toLowerCase()) ? ` – ${colour}` : ''}`, 60)} | Mithralaya`,
          description: clip(
            p.description || `Buy ${p.name}${p.material ? ` in ${p.material}` : ''} online at Mithralaya${low ? ` for ₹${low}` : ''}. Cash on delivery and easy 7-day returns.`,
            158
          ),
          path,
          image: p.images?.[0],
          type: 'product',
          jsonLd: {
            '@context': 'https://schema.org',
            '@type': 'Product',
            name: p.name,
            description: p.description || p.name,
            image: (p.images || []).map((img) => absUrl(img)),
            sku: p.variants?.[0]?.sku || String(p._id),
            brand: { '@type': 'Brand', name: p.brand || 'Mithralaya' },
            color: colour || undefined,
            material: p.material || undefined,
            offers: low
              ? {
                  '@type': 'Offer',
                  priceCurrency: 'INR',
                  price: low,
                  availability: inStock ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
                  itemCondition: 'https://schema.org/NewCondition',
                  url: absUrl(path)
                }
              : undefined
          }
        });
        trackViewItem({ ...p, price: low ?? p.price });

        // Recently viewed (this browser) — show the others, then remember this one
        setRecentlyViewed(getRecentlyViewed(p._id).slice(0, 10));
        addRecentlyViewed(p);

        // These don't block the page
        getProductReviews(p._id).then((r) => setReviews(r || [])).catch(() => setReviews([]));
        getAlsoBought(p._id).then((list) => setAlsoBought(list || [])).catch(() => setAlsoBought([]));
        getSizeChart({ category: p.category, subCategory: p.subCategory })
          .then((chart) => setSizeChart(chart))
          .catch(() => setSizeChart(null));

        // Size is intentionally left unselected — the person must pick
        // one themselves. Only color gets a sensible default.
        setSize("");
        setColor(p.colors?.[0] || "");

        // "You may also like" = same kind of product (kurtis with kurtis),
        // falling back to the same department if there are too few.
        let pool = [];
        if (p.subCategory) {
          pool = (await getTextileProducts({ subCategory: p.subCategory })).products || [];
        }
        if (pool.length < 5 && p.category) {
          const more = (await getTextileProducts({ category: p.category })).products || [];
          const seen = new Set(pool.map((x) => x._id));
          pool = [...pool, ...more.filter((x) => !seen.has(x._id))];
        }
        const related = pool
          .filter(item => item._id !== p._id && (!p.category || !item.category || item.category === p.category))
          .slice(0, 8);

        setRelatedProducts(related);
      } catch (err) {
        console.error(err);
      }
    };

    load();
  }, [id, isTextileProduct]);

  if (!product) return null;

  const discount =
    product.mrp && product.price
      ? Math.round(((product.mrp - product.price) / product.mrp) * 100)
      : 0;

  // true only when the product has sizes and every single one is sold out
  const allOutOfStock =
    product.sizes?.length > 0 &&
    product.sizes.every((s) => (product.sizeStock?.[s] ?? 1) === 0);

  // The variant matching the currently-selected size (falls back to the
  // first variant when nothing is picked yet), used to resolve
  // per-size fields like GST, SKU and warehouse.
  const activeVariant =
    product.variants?.find((v) => v.size === size) || product.variants?.[0] || null;

  const inWishlist = isInWishlist(product._id);
  const wishBusy = loadingIds.includes(String(product._id));
  const handleWishlist = async () => {
    if (!user) {
      navigate('/auth', { state: { from: window.location.pathname + window.location.search } });
      return;
    }
    try {
      const added = await toggleWishlist(product._id);
      addToast(added ? 'Added to your wishlist' : 'Removed from your wishlist', added ? 'success' : 'info');
    } catch (err) {
      addToast('Could not update your wishlist. Please try again.', 'error');
    }
  };

  const handlePrev = () =>
    setSelectedImageIndex((i) =>
      i === 0 ? (product.images?.length || 1) - 1 : i - 1
    );
  const handleNext = () =>
    setSelectedImageIndex((i) =>
      i === (product.images?.length || 1) - 1 ? 0 : i + 1
    );

  const handleAddToCart = async () => {
    if (allOutOfStock) return;
    if (product.sizes?.length > 0 && !size) {
      setShowSizePopup(true);
      return;
    }

    // Login illa-na — product details-a save panni login-ku redirect
    if (!user) {
      localStorage.setItem('pendingCartItem', JSON.stringify({
        productId: product._id,
        qty,
        size,
        color,
        catalogType: isTextileProduct ? 'textile' : 'legacy'
      }));
      navigate('/auth', { state: { from: window.location.pathname + window.location.search } });
      return;
    }

    try {
      await addToCart({
        productId: product._id,
        qty,
        size,
        color,
        catalogType: isTextileProduct ? "textile" : "legacy",
      });
      navigate("/cart");
    } catch (err) {
      console.log(err.response);
      addToast(err.response?.data?.message || err.message, 'error');
    }
  };

  const handleBuyNow = async () => {
    if (allOutOfStock) return;
    if (product.sizes?.length > 0 && !size) {
      setShowSizePopup(true);
      return;
    }

    const payload = {
      productId: product._id,
      qty,
      size,
      color,
      catalogType: isTextileProduct ? 'textile' : 'legacy'
    };

    if (!user) {
      localStorage.setItem('pendingCartItem', JSON.stringify(payload));
      navigate('/auth', {
        state: { from: window.location.pathname + window.location.search }
      });
      return;
    }

    await addToCart(payload);
    navigate('/cart');
  };

  const chestLabel = SIZE_CHEST_MAP[String(size).toUpperCase()] || '';

  // Key highlights from product data.
  // NOTE: "Sleeve" and "Style" pull from productType / sleeveOrStyle —
  // these were previously mislabeled as "Neck" (which showed sleeve
  // length like "Half Sleeve", not an actual neck type) and "style" was
  // captured on the product object but never rendered anywhere.
  // Only facts we actually have for this product — the old list filled
  // gaps with made-up defaults ("Regular Fit", "Casual", "Plain").
  const highlights = [
    { key: "Fabric", val: product.material },
    { key: "Product type", val: product.productType },
    { key: "Sleeve / style", val: product.sleeveOrStyle },
    { key: "Colour", val: (product.colors || []).join(", ") },
    { key: "Category", val: product.subCategory },
    { key: "Brand", val: product.brand },
    { key: "Fit", val: product.fit },
    { key: "Occasion", val: product.occasion },
    { key: "Pattern", val: product.pattern }
  ].filter((h) => h.val && String(h.val).trim() && h.val !== "-");

  return (
    <div className="pdp-page">
      {/* Breadcrumb */}
      <nav className="pdp-breadcrumb">
        <Link to="/">Home</Link>
        {product.category && (
          <><span>›</span><Link to={`/category/${product.category}`}>{product.category}</Link></>
        )}
        {product.subCategory && (
          <><span>›</span><span>{product.subCategory}</span></>
        )}
        {product.name && (
          <><span>›</span><span>{product.name}</span></>
        )}
      </nav>

      <div className="pdp-wrapper">
        {/* ── LEFT: Gallery ── */}
        <div className="pdp-gallery">
          {product.images?.length > 0 && (
            <div className="pdp-thumbnails">
              {product.images.map((img, i) => (
                <img
                  key={i}
                  src={mediaUrl(img)}
                  alt={`${product.name} ${i + 1}`}
                  className={`pdp-thumb ${i === selectedImageIndex ? 'active' : ''}`}
                  onClick={() => setSelectedImageIndex(i)}
                />
              ))}
            </div>
          )}

          <div className="pdp-main-image"
            onMouseMove={(e) => {
              const { left, top, width, height } = e.currentTarget.getBoundingClientRect();
              e.currentTarget.style.setProperty('--x', `${((e.clientX - left) / width) * 100}%`);
              e.currentTarget.style.setProperty('--y', `${((e.clientY - top) / height) * 100}%`);
            }}
          >

            {product.images?.length > 1 && (
              <button className="pdp-arrow pdp-arrow--left" onClick={handlePrev}>&#8249;</button>
            )}

            {product.images?.[selectedImageIndex] ? (
              <img
                src={mediaUrl(product.images[selectedImageIndex])}
                alt={product.name}
                className="zoom-image"
              />
            ) : (
              <div className="pdp-img-placeholder">Image</div>
            )}

            {product.images?.length > 1 && (
              <button className="pdp-arrow pdp-arrow--right" onClick={handleNext}>&#8250;</button>
            )}





           
          </div>
         
        </div>

        {/* ── RIGHT: Details ── */}
        <div className="pdp-info">

          {/* Title + Share */}
          {/* Product Category */}
          <div className="pdp-product-path">
            <span className="pdp-chip">{product.category}</span>

            {product.subCategory && (
              <>
                <span className="pdp-dot">•</span>
                <span className="pdp-chip">{product.subCategory}</span>
              </>
            )}

            {product.productType && (
              <>
                <span className="pdp-dot">•</span>
                <span className="pdp-chip">{product.productType}</span>
              </>
            )}
          </div>

          {/* Product Name */}
          <div className="pdp-title-row">
            <h1 className="pdp-title">{product.name}</h1>


          </div>

          {/* Brand */}
          <div className="pdp-brand-row">
            <span className="pdp-brand-label">by</span>
            <span className="pdp-brand-name">{product.brand}</span>
          </div>

          {/* SKU (per selected variant, falls back to first variant) */}


          {/* Price */}
          <div className="pdp-price-row">
            <span className="pdp-price">{formatPrice(product.price)}</span>
            {product.mrp > product.price && (
              <span className="pdp-mrp">{formatPrice(product.mrp)}</span>
            )}
            {discount > 0 && (
              <span className="pdp-discount">{discount}% OFF</span>
            )}
          </div>
          <p className="pdp-tax-note">Tax included.</p>

          {/* ── Offer Cards ── */}
          {/* <OfferCoupons price={product.price} /> */}

          <hr className="pdp-divider" />

          {/* ── Color Selector ── */}
          {product.colors?.length > 0 && (
            <div className="pdp-section">
              <p className="pdp-section-label">
                Color: <span style={{ fontWeight: 400 }}>{color}</span>
              </p>
              <div className="pdp-color-options">
                {product.colors.map((c) => (
                  <button
                    key={c}
                    title={c}
                    className={`pdp-color-swatch ${c === color ? 'active' : ''}`}
                    style={{ background: swatchBackground(c) }}
                    onClick={() => setColor(c)}
                  />
                ))}
              </div>
            </div>
          )}

          {product.colors?.length > 0 && <hr className="pdp-divider" />}

          {/* ── Size Selector ── */}
          {product.sizes?.length > 0 && (
            <div
              className={`pdp-size-section ${showSizePopup ? 'pdp-size-highlight' : ''}`}
              id="pdp-size-section"
            >
              <div className="pdp-size-header">
                <p className="pdp-size-label">
                  Size: <span>{size || '—'}</span>
                  {chestLabel && (
                    <span className="pdp-size-chest" style={{ marginLeft: 10 }}>{chestLabel}</span>
                  )}
                </p>
                <button type="button" className="pdp-size-guide" onClick={() => setShowSizeGuide(true)}>Size guide</button>
              </div>
              <div className="pdp-size-options">
                {product.sizes.map((s) => {
                  const inStock = (product.sizeStock?.[s] ?? 1) > 0;
                  return (
                    <button
                      key={s}
                      className={`pdp-size-btn ${s === size ? 'active' : ''} ${!inStock ? 'out-of-stock' : ''}`}
                      onClick={() => inStock && setSize(s)}
                      disabled={!inStock}
                      title={inStock ? s : `${s} — Out of stock`}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
              {allOutOfStock && (
                <p className="pdp-oos-note">This product is currently out of stock.</p>
              )}
            </div>
          )}

          <hr className="pdp-divider" />

          {/* ── Add to bag + wishlist ── */}
          <div className="m-pdp-buy-row">
            <button
              onClick={handleAddToCart}
              className="pdp-btn pdp-btn--cart"
              disabled={allOutOfStock}
            >
              {allOutOfStock ? 'Out of stock' : 'Add to bag'}
            </button>
            <button
              type="button"
              className={`m-pdp-wish ${inWishlist ? 'is-active' : ''}`}
              onClick={handleWishlist}
              disabled={wishBusy}
              aria-pressed={inWishlist}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill={inWishlist ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 20.5s-7.5-4.6-9.2-9.4C1.6 7.6 3.9 4 7.4 4c2 0 3.5 1.1 4.6 2.7C13.1 5.1 14.6 4 16.6 4c3.5 0 5.8 3.6 4.6 7.1-1.7 4.8-9.2 9.4-9.2 9.4z" />
              </svg>
              {inWishlist ? 'Wishlisted' : 'Wishlist'}
            </button>
          </div>

          {/* Easy return note */}
          {/* ── Delivery Check ── */}
          <DeliveryCheck price={product.price} />

          {/* ── What you get ── */}
          <ul className="m-pdp-assurances">
            <li>Cash on delivery available</li>
            <li>Easy 7-day returns</li>
            <li>Secure payments by Razorpay</li>
          </ul>

          {/* ── Key Highlights ── */}
          {highlights.length > 0 && (
            <>
              <p className="pdp-highlights-title">Key Highlights</p>
              <div className="pdp-highlights-grid">
                {highlights.map(h => (
                  <div className="pdp-highlight-cell" key={h.key}>
                    <span className="pdp-highlight-key">{h.key}</span>
                    <span className="pdp-highlight-val">{h.val}</span>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* ── Product Description Accordion ── */}
          <p className="pdp-accordion-title">Product Description</p>
          <div className="pdp-accordion">
            {ACCORDION_SECTIONS.map(({ key, icon, title, subtitle }) => (
              <div className="accordion-item" key={key}>
                <button
                  className="accordion-header"
                  onClick={() => toggleAccordion(key)}
                >
                  <div className="accordion-header-left">
                    <span className="accordion-icon">{icon}</span>
                    <div className="accordion-header-text">
                      <span className="accordion-header-title">{title}</span>
                      {subtitle && (
                        <span className="accordion-header-subtitle">{subtitle}</span>
                      )}
                    </div>
                  </div>
                  <span className="accordion-toggle">
                    {activeAccordion === key ? '−' : '+'}
                  </span>
                </button>

                {activeAccordion === key && (
                  <div className="accordion-body">
                    {key === "details" && (
                      <>
                        <p>{product.description}</p>

                        <h4>Product Details</h4>

                        <table className="pdp-spec-table">
                          <tbody>

                            {product.brand && (
                              <tr>
                                <td>Brand</td>
                                <td>{product.brand}</td>
                              </tr>
                            )}

                            <tr>
                              <td>Category</td>
                              <td>{product.category}</td>
                            </tr>

                            <tr>
                              <td>Sub Category</td>
                              <td>{product.subCategory}</td>
                            </tr>

                            {product.productType && (
                              <tr>
                                <td>Product Type</td>
                                <td>{product.productType}</td>
                              </tr>
                            )}

                            {product.material && (
                              <tr>
                                <td>Material</td>
                                <td>{product.material}</td>
                              </tr>
                            )}

                            <tr>
                              <td>Color</td>
                              <td>{color}</td>
                            </tr>

                            <tr>
                              <td>Available Sizes</td>
                              <td>
                                {product.sizes
                                  ?.filter((s) => (product.sizeStock?.[s] ?? 1) > 0)
                                  .join(", ") || "Out of stock"}
                              </td>
                            </tr>

                            {/* GST lives per-variant, not on the product itself —
                                resolve it from the variant matching the selected
                                size (or the first variant as a fallback). */}


                            {/* This used to show product.slug, which is not a
                                SKU — the real per-size SKU lives on the variant. */}
                            <tr>
                              <td>SKU</td>
                              <td>{activeVariant?.sku || "-"}</td>
                            </tr>

                          </tbody>
                        </table>
                      </>
                    )}
                    {key === "disclaimer" && (
                      <>
                        <p>
                          Actual product color may vary slightly because of
                          photographic lighting and screen settings.
                          Measurements may vary by ±5%.
                        </p>
                      </>
                    )}
                    {key === "washcare" && (
                      <>
                        <ul>
                          {washCareFor(product).map((line) => <li key={line}>{line}</li>)}
                        </ul>
                      </>
                    )}
                    {key === "returns" && (
                      <>
                        <ul>
                          <li>Easy {returnDays}-day returns from the date of delivery</li>
                          <li>Unused product with tags only.</li>
                          <li>Refund to original payment method.</li>
                          <li>Replacement available for damaged products.</li>
                        </ul>
                      </>
                    )}
                    {key === "manufacturing" && (
                      <>
                        <table className="pdp-spec-table">
                          <tbody>

                            <tr>
                              <td>Brand</td>
                              <td>{product.brand}</td>
                            </tr>

                            <tr>
                              <td>Marketed by</td>
                              <td>Mithralaya, Chennai, Tamil Nadu</td>
                            </tr>
                            {(product.supplierName || activeVariant?.supplierName) && (
                              <tr>
                                <td>Manufacturer</td>
                                <td>{product.supplierName || activeVariant?.supplierName}</td>
                              </tr>
                            )}

                            <tr>
                              <td>Country of Origin</td>
                              <td>India</td>
                            </tr>

                          </tbody>
                        </table>
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* More About / FAQ */}
          <div className="pdp-extra-accordions">
            {[
              { key: 'moreabout', label: 'About this product' },
              { key: 'faq', label: 'FAQ' },
            ].map(({ key, label }) => (
              <div className="accordion-item" key={key}>
                <button
                  className="accordion-header"
                  onClick={() => toggleAccordion(key)}
                >
                  <span style={{ fontWeight: 500, fontSize: 14 }}>{label}</span>
                  <span className="accordion-toggle">
                    {activeAccordion === key ? '−' : '+'}
                  </span>
                </button>
                {activeAccordion === key && (
                  <div className="accordion-body">
                    {key === 'moreabout' && (
                      <p>{product.description || `${product.name}${product.material ? ` in ${product.material}` : ''}.`}</p>
                    )}
                    {key === 'faq' && (
                      <>
                        <p><strong>Q: Is this product true to size?</strong><br />A: Yes, the product follows standard sizing. Refer to the Size Guide for measurements.</p>
                        <p><strong>Q: What is the return policy?</strong><br />A: Easy {returnDays}-day returns from delivery. Items must be unused with the original tags attached.</p>
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

        </div>
      </div>

      {/* Ratings & reviews (photos, verified buyers) */}
      <ReviewsSection productId={product._id} productName={product.name} reviews={reviews} />

      {relatedProducts.length > 0 && (
        <section className="related-products-section">
          <div className="related-header">
            <h2>Related Products</h2>
            <p>You may also like these products</p>
          </div>

          <div className="related-products-grid">
            {relatedProducts.map(product => (
              <ProductCard
                key={product._id}
                product={{
                  ...product,
                  catalogType: "textile",
                }}
              />
            ))}
          </div>
        </section>
      )}

      <ProductRail
        title="Customers also bought"
        note="Often bought together with this item"
        products={alsoBought}
      />

      <ProductRail
        title="Recently viewed"
        products={recentlyViewed}
      />

      {/* ===== SIZE GUIDE ===== */}
      {showSizeGuide && (
        <div className="size-popup-overlay" onClick={() => setShowSizeGuide(false)}>
          <div className="size-popup m-size-guide" role="dialog" aria-label="Size guide" onClick={(e) => e.stopPropagation()}>
            <button className="size-popup-close" onClick={() => setShowSizeGuide(false)} aria-label="Close">×</button>
            <h3>Size guide</h3>
            {product.sizeGuideImage || sizeChart?.image ? (
              <img src={mediaUrl(product.sizeGuideImage || sizeChart.image)} alt={`Size chart for ${product.name}`} style={{ width: '100%' }} />
            ) : sizeChart?.rows?.length ? (
              <>
                <p className="m-size-sub">
                  {sizeChart.title}{sizeChart.unit && !/saree/i.test(sizeChart.title) ? ` · measurements in ${sizeChart.unit === 'cm' ? 'centimetres' : 'inches'}` : ''}
                </p>
                <div className="m-size-scroll">
                  <table className="m-size-table">
                    <thead>
                      <tr><th>Size</th>{sizeChart.columns.map((c) => <th key={c}>{c}</th>)}</tr>
                    </thead>
                    <tbody>
                      {sizeChart.rows.map((r) => (
                        <tr key={r.size} className={size && String(r.size).toUpperCase().split(/[\s/]+/).includes(String(size).toUpperCase()) ? 'is-current' : ''}>
                          <td>{r.size}</td>{r.values.map((v, i) => <td key={i}>{v}</td>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {sizeChart.howToMeasure && <p className="m-size-how"><strong>How to measure:</strong> {sizeChart.howToMeasure}</p>}
                {sizeChart.note && <p className="m-size-how">{sizeChart.note}</p>}
              </>
            ) : (
              <>
                <p>Body measurements in inches. If you are between sizes, choose the larger one.</p>
                <table className="m-size-table">
                  <thead>
                    <tr><th>Size</th><th>Bust</th><th>Waist</th><th>Hip</th></tr>
                  </thead>
                  <tbody>
                    {SIZE_CHART.map((r) => (
                      <tr key={r.size} className={String(size).toUpperCase() === r.size ? 'is-current' : ''}>
                        <td>{r.size}</td><td>{r.bust}</td><td>{r.waist}</td><td>{r.hip}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>
        </div>
      )}

      {/* ===== SELECT SIZE POPUP ===== */}
      {showSizePopup && (
        <div className="size-popup-overlay" onClick={() => setShowSizePopup(false)}>
          <div className="size-popup" onClick={(e) => e.stopPropagation()}>
            <button
              className="size-popup-close"
              onClick={() => setShowSizePopup(false)}
              aria-label="Close"
            >
              ×
            </button>

            <h3>Choose your size</h3>
            <p>Pick your size below to continue adding this item to your cart.</p>

            <div className="size-popup-options">
              {product.sizes.map((s) => {
                const inStock = (product.sizeStock?.[s] ?? 1) > 0;
                return (
                  <button
                    key={s}
                    className={`size-popup-btn ${!inStock ? 'out-of-stock' : ''}`}
                    disabled={!inStock}
                    onClick={() => {
                      setSize(s);
                      setShowSizePopup(false);
                    }}
                  >
                    {s}
                  </button>
                );
              })}
            </div>

            <button
              className="size-popup-dismiss"
              onClick={() => setShowSizePopup(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetailsPage;
